import { nanoid } from 'nanoid';
import type { Plan, TrainingGoal, TrainingLevel, PlanTemplate } from '../types';
import { EXERCISE_MAP } from './exercises';
import { createExerciseInstance, createSession, createWeek } from '../utils/helpers';

// ── Helper to build a prefilled plan from chosen exercises ──

function buildTemplatePlan(
  name: string,
  goal: TrainingGoal,
  level: TrainingLevel,
  frequency: number,
  mesocycleLength: number,
  sessionsData: { day: string; focus: string; exerciseIds: string[] }[]
): Plan {
  const week = createWeek(1);
  week.sessions = sessionsData.map((s) => {
    const session = createSession(s.day, s.focus);
    session.exercises = s.exerciseIds
      .map((eid) => {
        const def = EXERCISE_MAP.get(eid);
        if (!def) return null;
        return createExerciseInstance(def);
      })
      .filter(Boolean) as any[];
    return session;
  });

  const weeks = [week];
  for (let i = 2; i <= mesocycleLength; i++) {
    const w = createWeek(i);
    w.sessions = week.sessions.map((s) => ({
      ...s,
      id: nanoid(),
      warmupExercises: (s.warmupExercises || []).map((e) => ({ ...e, id: nanoid() })),
      exercises: s.exercises.map((e) => ({ ...e, id: nanoid() })),
    }));
    if (i === mesocycleLength) w.deload = true;
    weeks.push(w);
  }

  return {
    id: nanoid(),
    name,
    goal,
    trainingLevel: level,
    mesocycleLength,
    frequency,
    fatigueTolerance: 6,
    progressionModel: goal === 'strength' ? 'linear' : 'undulating',
    weeks,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

// ═══════════════════════════════════════════
// TEMPLATE DEFINITIONS
// ═══════════════════════════════════════════

export const PLAN_TEMPLATES: PlanTemplate[] = [
  {
    id: 'ppl-6day',
    name: 'Push / Pull / Nogi (6 dni)',
    description:
      'Klasyczny bodybuilderski podział trenujący każdą grupę mięśniową dwa razy w tygodniu. Idealny dla średniozaawansowanych skupionych na hipertrofii.',
    goal: 'hypertrophy',
    level: 'intermediate',
    frequency: 6,
    factory: () =>
      buildTemplatePlan('Push / Pull / Nogi', 'hypertrophy', 'intermediate', 6, 4, [
        {
          day: 'Poniedziałek',
          focus: 'Pchanie A',
          exerciseIds: ['barbell-bench-press', 'incline-db-press', 'overhead-press', 'lateral-raise', 'tricep-pushdown', 'cable-fly'],
        },
        {
          day: 'Wtorek',
          focus: 'Ciągnięcie A',
          exerciseIds: ['barbell-row', 'pull-up', 'cable-row', 'face-pull', 'barbell-curl', 'hammer-curl'],
        },
        {
          day: 'Środa',
          focus: 'Nogi A',
          exerciseIds: ['back-squat', 'romanian-deadlift', 'leg-press', 'leg-curl', 'standing-calf-raise', 'hanging-leg-raise'],
        },
        {
          day: 'Czwartek',
          focus: 'Pchanie B',
          exerciseIds: ['incline-barbell-press', 'dumbbell-bench-press', 'db-shoulder-press', 'lateral-raise', 'overhead-tricep-ext', 'pec-deck'],
        },
        {
          day: 'Piątek',
          focus: 'Ciągnięcie B',
          exerciseIds: ['t-bar-row', 'lat-pulldown', 'chest-supported-row', 'rear-delt-fly', 'hammer-curl', 'farmers-walk'],
        },
        {
          day: 'Sobota',
          focus: 'Nogi B',
          exerciseIds: ['front-squat', 'hip-thrust', 'bulgarian-split-squat', 'leg-extension', 'seated-calf-raise', 'pallof-press'],
        },
      ]),
  },
  {
    id: 'upper-lower-4day',
    name: 'Góra / Dół (4 dni)',
    description:
      'Zrównoważony 4-dniowy podział z 2 sesjami góry i 2 dołu. Świetny dla średniozaawansowanych potrzebujących solidnej regeneracji.',
    goal: 'general',
    level: 'intermediate',
    frequency: 4,
    factory: () =>
      buildTemplatePlan('Góra / Dół', 'general', 'intermediate', 4, 4, [
        {
          day: 'Poniedziałek',
          focus: 'Góra A (Siła)',
          exerciseIds: ['barbell-bench-press', 'barbell-row', 'overhead-press', 'pull-up', 'barbell-curl', 'tricep-pushdown'],
        },
        {
          day: 'Wtorek',
          focus: 'Dół A (Siła)',
          exerciseIds: ['back-squat', 'conventional-deadlift', 'leg-press', 'leg-curl', 'standing-calf-raise', 'ab-wheel'],
        },
        {
          day: 'Czwartek',
          focus: 'Góra B (Objętość)',
          exerciseIds: ['incline-db-press', 'cable-row', 'db-shoulder-press', 'lat-pulldown', 'lateral-raise', 'face-pull'],
        },
        {
          day: 'Piątek',
          focus: 'Dół B (Objętość)',
          exerciseIds: ['front-squat', 'romanian-deadlift', 'bulgarian-split-squat', 'leg-extension', 'seated-calf-raise', 'pallof-press'],
        },
      ]),
  },
  {
    id: 'fullbody-3day',
    name: 'Całe ciało (3 dni)',
    description:
      'Trzy sesje treningowe całego ciała tygodniowo. Idealne dla początkujących lub osób z ograniczonym czasem.',
    goal: 'general',
    level: 'beginner',
    frequency: 3,
    factory: () =>
      buildTemplatePlan('Całe ciało', 'general', 'beginner', 3, 4, [
        {
          day: 'Poniedziałek',
          focus: 'Całe ciało A',
          exerciseIds: ['back-squat', 'barbell-bench-press', 'barbell-row', 'overhead-press', 'romanian-deadlift', 'plank'],
        },
        {
          day: 'Środa',
          focus: 'Całe ciało B',
          exerciseIds: ['front-squat', 'dumbbell-bench-press', 'lat-pulldown', 'db-shoulder-press', 'hip-thrust', 'hanging-leg-raise'],
        },
        {
          day: 'Piątek',
          focus: 'Całe ciało C',
          exerciseIds: ['leg-press', 'incline-db-press', 'dumbbell-row', 'lateral-raise', 'leg-curl', 'ab-wheel'],
        },
      ]),
  },
  {
    id: 'strength-4day',
    name: 'Blok siłowy (4 dni)',
    description:
      'Blok treningowy ukierunkowany na powerlifting z naciskiem na wielką trójkę. Intensywność kierowana RPE dla zaawansowanych.',
    goal: 'strength',
    level: 'advanced',
    frequency: 4,
    factory: () =>
      buildTemplatePlan('Blok siłowy', 'strength', 'advanced', 4, 6, [
        {
          day: 'Poniedziałek',
          focus: 'Przysiad + akcesoria',
          exerciseIds: ['back-squat', 'front-squat', 'leg-press', 'leg-curl', 'standing-calf-raise', 'plank'],
        },
        {
          day: 'Wtorek',
          focus: 'Ławka + góra',
          exerciseIds: ['barbell-bench-press', 'incline-barbell-press', 'dips', 'barbell-row', 'lateral-raise', 'tricep-pushdown'],
        },
        {
          day: 'Czwartek',
          focus: 'Martwy ciąg + tylny łańcuch',
          exerciseIds: ['conventional-deadlift', 'romanian-deadlift', 'barbell-row', 'pull-up', 'face-pull', 'ab-wheel'],
        },
        {
          day: 'Piątek',
          focus: 'Wariant ławki + objętość',
          exerciseIds: ['dumbbell-bench-press', 'overhead-press', 'cable-row', 'lat-pulldown', 'barbell-curl', 'rear-delt-fly'],
        },
      ]),
  },
];
